import { AxiosError } from "axios";

// Module-level "ensure the cached access token is fresh" hook, set by React
// code via setTokenRefresher() (mirrors setOnUnauthorized in axiosConfig). The
// app registers a closure that proactively refreshes the Kinde access token
// when it's near expiry - necessary because Kinde v5's getToken() is a plain
// read of the cached token and no longer refreshes on its own. Kept here (not
// threaded through all 170+ getAuthTokenBlocking call sites) so the api-client
// stays Kinde-agnostic.
let tokenRefresher: (() => Promise<void>) | null = null;

// Coalesce concurrent callers onto a single refresh: a list page fires many
// queries at once, and without this each would kick off its own refresh of the
// same (rotating, single-use) refresh token, racing each other into failure.
let inflightRefresh: Promise<void> | null = null;

// Module-level "is this cached token actually usable" check, set by React code
// via setTokenValidator() (mirrors setTokenRefresher). The app registers a
// closure that knows its Kinde client id and decodes azp/exp. Without it we'd
// only know a token is *present*, not *valid* - and Kinde v5's getToken() hands
// back a present-but-invalid token (expired, or minted by another client) it
// never clears, which would 401-loop behind the "Session expired" modal instead
// of blocking on relogin. Kept here so the api-client stays Kinde-agnostic.
let tokenValidator: ((token: string) => boolean) | null = null;

export const setTokenRefresher = (
  refresher: (() => Promise<void>) | null
): void => {
  tokenRefresher = refresher;
};

export const setTokenValidator = (
  validator: ((token: string) => boolean) | null
): void => {
  tokenValidator = validator;
};

// Usable = present AND (no validator registered, or the validator approves it).
const isTokenUsable = (token: string | undefined): token is string =>
  !!token && (tokenValidator ? tokenValidator(token) : true);

export const getAuthTokenBlocking = async (
  getToken: () => Promise<string | undefined>,
  relogin: (() => Promise<void>) | undefined
) => {
  if (tokenRefresher) {
    if (!inflightRefresh) {
      inflightRefresh = (async () => {
        try {
          await tokenRefresher!();
        } finally {
          inflightRefresh = null;
        }
      })();
    }
    try {
      await inflightRefresh;
    } catch {
      // Refresh failed (e.g. the refresh token itself expired). Fall through
      // to the getToken + relogin path below, which surfaces the relogin modal.
    }
  }

  let token = await getToken();

  // Block on relogin when the token is absent OR present-but-invalid (expired /
  // wrong client). Gating on usability is what makes the relogin modal actually
  // halt API calls instead of letting a stale token 401-loop behind it.
  if (!isTokenUsable(token) && relogin) {
    await relogin();
    token = await getToken();
  }

  if (!isTokenUsable(token)) throw new Error("NO_TOKEN");
  return token;
};

/**
 * Extracts the best possible error message from:
 * - React Query error objects
 * - Axios errors with JSON or blob bodies
 * - Any unknown thrown values
 */
export const extractAxiosErrorMessage = async (
  error: unknown
): Promise<string> => {
  // 1. AxiosError with a direct JSON message
  const axiosErr = error as AxiosError;

  // Try: error.response.data.message, then BMA's usual {"error": ...} body
  const messageFromJSON =
    axiosErr?.response?.data &&
    typeof axiosErr.response.data === "object" &&
    ((axiosErr.response.data as any).message ??
      (axiosErr.response.data as any).error);

  if (messageFromJSON) {
    return String(messageFromJSON);
  }

  // 2. AxiosError with a blob body (common when responseType: "blob")
  const data = axiosErr?.response?.data;
  if (data instanceof Blob) {
    try {
      const text = await data.text();
      try {
        const parsed = JSON.parse(text);
        if (parsed?.message) return String(parsed.message);
        return text; // JSON but no `message`
      } catch {
        return text; // Not JSON, return raw text
      }
    } catch {
      /* fall through to fallback */
    }
  }

  // 3. Axios error message field
  if (axiosErr?.message) {
    return axiosErr.message;
  }

  // 4. Plain Error instance
  if (error instanceof Error) {
    return error.message;
  }

  // 5. Fallback: stringify anything
  try {
    return JSON.stringify(error);
  } catch {
    return String(error);
  }
};
